import React, { useState } from 'react';
import {
  Box,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { IconAt, IconPlus } from '@tabler/icons-react';
import { CollapsibleSection } from '../tickets/Customer/CollapsibleSection';
import { SECONDARY_BTN_SX, type PanelDensity } from '../tickets/Customer/panelStyles';
import type {
  CustomerAlias,
  CreateCustomerAliasRequest,
  UpdateCustomerAliasRequest,
  Vendor,
} from '@techgitter/contracts';
import { AliasTable } from './AliasTable';
import { AddEditAliasModal } from './AddEditAliasModal';

interface CustomerAliasesPanelProps {
  aliases: CustomerAlias[];
  vendors: Vendor[];
  isLoading: boolean;
  error: string | null;
  canEditMatchingAliases: boolean;
  canDeleteAliases: boolean;
  canToggleActive: boolean;
  refresh: () => void;
  create: (payload: CreateCustomerAliasRequest) => Promise<void>;
  update: (aliasId: string, payload: UpdateCustomerAliasRequest) => Promise<void>;
  remove: (aliasId: string) => Promise<void>;
  /** Density tier — `comfortable` (customer-detail card) or `compact`
   *  (default; New Ticket sidebar). Forwarded to the alias table. */
  density?: PanelDensity;
}

export const CustomerAliasesPanel: React.FC<CustomerAliasesPanelProps> = ({
  aliases,
  vendors,
  error,
  canEditMatchingAliases,
  canDeleteAliases,
  canToggleActive,
  refresh,
  create,
  update,
  remove,
  density = 'compact',
}) => {
  const [expanded, setExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [editTarget, setEditTarget] = useState<CustomerAlias | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleAdd = () => {
    setModalMode('add');
    setEditTarget(null);
    setModalOpen(true);
  };

  const handleEdit = (alias: CustomerAlias) => {
    setModalMode('edit');
    setEditTarget(alias);
    setModalOpen(true);
  };

  const handleToggleActive = async (aliasId: string, isActive: boolean) => {
    await update(aliasId, { isActive });
  };

  const handleDeleteRequest = (aliasId: string) => {
    setDeleteConfirmId(aliasId);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;
    try {
      await remove(deleteConfirmId);
      setDeleteConfirmId(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete alias');
    }
  };

  const aliasToDelete = deleteConfirmId
    ? aliases.find((a) => a.aliasId === deleteConfirmId)
    : null;

  return (
    <>
      <CollapsibleSection
        icon={IconAt}
        title="Aliases"
        count={aliases.length}
        onRefresh={refresh}
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
      >
          {canEditMatchingAliases && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<IconPlus size={16} stroke={1.5} />}
                onClick={handleAdd}
                sx={SECONDARY_BTN_SX}
              >
                Add alias
              </Button>
            </Box>
          )}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                <Button color="inherit" size="small" onClick={refresh}>
                  Retry
                </Button>
              }
            >
              {error}
            </Alert>
          )}

          <AliasTable
            aliases={aliases}
            vendors={vendors}
            canEditMatchingAliases={canEditMatchingAliases}
            canDeleteAliases={canDeleteAliases}
            canToggleActive={canToggleActive}
            onEdit={handleEdit}
            onToggleActive={handleToggleActive}
            onDelete={handleDeleteRequest}
            density={density}
          />
      </CollapsibleSection>

      <AddEditAliasModal
        open={modalOpen}
        mode={modalMode}
        editAlias={editTarget}
        vendors={vendors}
        onClose={() => setModalOpen(false)}
        onSubmitCreate={create}
        onSubmitUpdate={update}
      />

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Alias</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText>
            Are you sure you want to permanently delete the alias
            {aliasToDelete ? ` "${aliasToDelete.alias}"` : ''}?
            Consider deactivating it instead to preserve audit history.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmId(null)}
            sx={{ color: '#aaaaaa', '&:hover': { color: '#555555', bgcolor: 'transparent' } }}
          >
            Cancel
          </Button>
          {aliasToDelete?.isActive && (
            <Button
              onClick={async () => {
                if (deleteConfirmId) {
                  try {
                    await update(deleteConfirmId, { isActive: false });
                    setDeleteConfirmId(null);
                  } catch (err) {
                    setDeleteError(err instanceof Error ? err.message : 'Failed to deactivate');
                  }
                }
              }}
              variant="outlined"
            >
              Deactivate Instead
            </Button>
          )}
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
